import { createContext, useEffect, useState } from 'react';

import { ResponsiveLoader } from 'components/responsive-loader/ResponsiveLoader';
import { usePublicationSettings } from 'hooks/publication-settings';
import noop from 'utils/noop';
import { AltSummVariant } from './summarian.utils';

export type { AltSummVariant };

export const DEFAULT_ALT_SUM_ID = 'U.S. Home';

export interface SummarianContext {
  isSummarianReady: boolean;
  isSummarianRetrying: boolean;
  selectedAltSummVariant: AltSummVariant;
  setSelectedAltSummVariant: (selectedAltSummVariant: AltSummVariant) => void;
}

const DEFAULT_SUMMARIAN_STATE: SummarianContext = {
  isSummarianReady: false,
  isSummarianRetrying: false,
  selectedAltSummVariant: 'U.S. Home',
  setSelectedAltSummVariant: noop
};

interface SummarianProviderProps {
  children: React.ReactNode;
  initialAltSummVariant?: AltSummVariant;
}

export const SummarianContext = createContext(DEFAULT_SUMMARIAN_STATE);

export const SummarianProvider = ({ children, initialAltSummVariant }: SummarianProviderProps) => {
  const [isSummarianSettled, setIsSummarianSettled] = useState(window.hasSummarianSettled);
  const [isSummarianReady, setIsSummarianReady] = useState(window.isSummarianReady ?? false);
  const [isSummarianScriptRetryExhausted, setIsSummarianScriptRetryExhausted] = useState<boolean>(false);
  const { data: publicationSettingsResp } = usePublicationSettings();
  const priorityAltSum =
    initialAltSummVariant ??
    (publicationSettingsResp?.publicationSetting.defaultAltSumm as AltSummVariant | undefined) ??
    DEFAULT_ALT_SUM_ID;
  const [selectedAltSummVariant, setSelectedAltSummVariant] = useState<AltSummVariant>(priorityAltSum);

  useEffect(() => {
    const onSummarianSettled = () => {
      setIsSummarianSettled(true);
    };

    // Catch the case where the event fired between initial render and this effect running
    if (window.hasSummarianSettled) {
      setIsSummarianSettled(true);
    }

    window.summarianSettledEventTarget.addEventListener('summarianSettled', onSummarianSettled);

    return () => {
      window.summarianSettledEventTarget.removeEventListener('summarianSettled', onSummarianSettled);
    };
  }, []);

  useEffect(() => {
    const onSummarianReady = () => {
      setIsSummarianReady(true);
    };

    // Catch the case where the event fired between initial render and this effect running
    if (window.isSummarianReady) {
      setIsSummarianReady(true);
    }

    window.summarianReadyEventTarget.addEventListener('summarianReady', onSummarianReady);

    return () => {
      window.summarianReadyEventTarget.removeEventListener('summarianReady', onSummarianReady);
    };
  }, []);

  useEffect(() => {
    const onScriptRetryExhausted = () => {
      setIsSummarianScriptRetryExhausted(true);
    };

    window.summarianScriptRetryExhaustedEventTarget.addEventListener(
      'summarianScriptRetryExhausted',
      onScriptRetryExhausted
    );

    return () => {
      window.summarianScriptRetryExhaustedEventTarget.removeEventListener(
        'summarianScriptRetryExhausted',
        onScriptRetryExhausted
      );
    };
  }, []);

  useEffect(() => {
    setSelectedAltSummVariant(priorityAltSum);
  }, [priorityAltSum]);

  if (!isSummarianSettled) {
    return <ResponsiveLoader />;
  }

  const scriptStillRetrying = !isSummarianScriptRetryExhausted && window.isSummarianScriptRetrying;
  const isSummarianRetrying =
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    !isSummarianReady && (scriptStillRetrying || (!!window.summarianShareDbAPI && !!window.isSummarianSettingsLoaded));

  const value = {
    isSummarianReady,
    isSummarianRetrying,
    selectedAltSummVariant,
    setSelectedAltSummVariant
  };
  return <SummarianContext.Provider value={value}>{children}</SummarianContext.Provider>;
};
